<template>
	<div class="w-100">
		<FinancialDataSummary 
			v-if="showSummary" 
			:myAccount="myAccountData"
			:bankAccount="bankAccount"
			:identityValidation="identityValidation"
			@edit="handleEditMode" 
		/>
		<DocumentStatus v-if="finishedFormStep && !modeEdit"/>
		<div class="d-flex flex-column" style="gap: 32px;">
			<div v-if="(!selectedAccountType && isPending)" class="d-flex flex-column" style="gap: 24px;">
				<div class="choose-account">
					<div class="d-flex flex-column" style="gap: 16px">
						<div class="title">
							<h5>{{ $t("views.seller.my_account.text_1238") }}</h5>
						</div>
						<div class="d-flex w-100" style="gap: 24px;">
							<div class="d-flex w-100" style="gap: 24px;">
								<label v-for="(accountType, index) in accountsType" :key="index"
									:class="`choose-accoount-option ${currentAccountType.id === accountType.id ? 'active' : ''}`"
									@click="handleChecked(accountType)">
									<input :id="accountType.id" type="radio" :checked="isCurrentAccountType(accountType, index)"
										class="d-none" />

									<div>
										<p class="leading-6">{{ accountType.title }}</p>
										<p class="leading-5">{{ accountType.subtitle }}</p>
									</div>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
										class="icon-tick">
										<path
											d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z"
											fill="#D1D5DB" />
									</svg>
								</label>
							</div>
						</div>
					</div>
				</div>

				<div class="w-100 d-flex justify-content-end">
					<BaseNewButton 
						:disabled="currentAccountType.id === ''" 
						variant="primary" 
						icon="arrow-right-outline"
						iconPosition="right" 
						type="button"
						@click="handleSelectedAccountType"
					>
						{{$t("views.seller.my_account.text_1243") }}
					</BaseNewButton>
				</div>
			</div>

      <FinancialDataFormSteps 
				v-if="showFormSteps" 
				:accountType="selectedAccountType" 
				@back="handleBack" 
				ref="FinancialDataFormSteps"
			/>

			<div v-if="isPending" class="note-warning">
				<img src="@/assets/img/icons/exclamation-circle.svg" alt="">
				<p>
					{{ $t("views.seller.my_account.text_1244") }}
				</p>
			</div>
		</div>
	</div>
</template>

<script>
import DocumentStatus from './DocumentStatus.vue';
import FinancialDataFormSteps from './FinancialDataFormSteps.vue';
import FinancialDataSummary from './FinancialDataSummary.vue';
import BankAccountService from "@/services/resources/BankAccountService";

const bankAccountService = BankAccountService.build();

export default {
	name: 'FinancialDataTab',
  components: {
    FinancialDataFormSteps,
		FinancialDataSummary,
		DocumentStatus,
  },
	data() {
		return {
			accountsType: [
				{ id: 'personal_account', title: this.$t('views.seller.my_account.text_1239'), subtitle: this.$t('views.seller.my_account.text_1240') },
				{ id: 'legal_account', title: this.$t('views.seller.my_account.text_1241'), subtitle: this.$t('views.seller.my_account.text_1242') },
			],
			currentAccountType: { id: '' },
			selectedAccountType: '',
			finishedFormStep: false,
			bankAccount: null,
			myAccountData: null,
			identityValidation: {
				status: 'INITIATED'
			},
      modeEdit: false
		}
	},
	computed: {
		showFormSteps() {
      if (this.modeEdit) {
        return true;
      }
			return (this.selectedAccountType && !this.finishedFormStep);
		},
		isPending() {
      if (!this.bankAccount) {
        return true;
      }
      if (this.bankAccount.status === null) {
        return true;
      }
			if (!this.bankAccount.external_account_id) {
				return true; 
			}
      if (['PROCESSING', 'APPROVED', 'DISAPPROVED'].includes(this.bankAccount.status)) {
        return false;
      }
      return true;
		},
    showSummary() {
      if (this.finishedFormStep) {
        return false;
      }
      if (this.modeEdit) {
        return false;
      }
      if (this.isPending) {
        return false;
      }
      if (!this.myAccountData) {
        return false;
      }
      return true;
    }
	},
	methods: {
		handleChecked(accountType) {
			this.currentAccountType = accountType;
		},
		isCurrentAccountType(account, index) {
			return this.currentAccountType
				? this.currentAccountType.id === account.id
				: index === 0;
		},
		handleSelectedAccountType() {
			this.selectedAccountType = this.currentAccountType.id;

			this.$root.accountType = 'PERSONAL';

			if (this.currentAccountType.id === 'legal_account') {
				this.$root.accountType = 'BUSINESS';
			}
		},
    handleEditMode(isEdit) {
      this.$root.isEditMode = isEdit;
    },
    handleFinish(isFinished) {
			this.finishedFormStep = isFinished;
      this.fetchBankAccount();
    },
		async fetchBankAccount() {
			try {
				const bankAccounts = await bankAccountService
					.search();

				if (bankAccounts && bankAccounts.length) {
					this.bankAccount = bankAccounts[bankAccounts.length - 1];
          this.$root.$emit('onload-bank-account', this.bankAccount);
					return this.bankAccount
				}
				return {};
			} catch (error) {
				console.log('FinancialDataTab - fetchBankAccount', error);
				return {};
			}
		},
    handleBack({ modeEdit }) {
      this.selectedAccountType = '';
      this.modeEdit = modeEdit;
      this.finishedFormStep = false;
    },
	},
  async mounted() {		
		this.$root.$on('onload-my-account', async data => {
			this.myAccountData = {
				...data,
				identityValidation:  data.identityValidation,
				bankAccount: await this.fetchBankAccount()
			};

			this.identityValidation = data.identityValidation;
			this.$root.myAccountData = this.myAccountData;
		});
  },
  created() {
    this.$root.$on('on-finish', this.handleFinish);
    this.$root.$on('on-edit', ({ step, isBusiness }) => {
      this.$root.currentStep = step;
      this.modeEdit = true;
      this.$root.modeEdit = true;
      
      if (isBusiness) {
        this.selectedAccountType = this.accountsType[1].id;
      } else {
        this.selectedAccountType = this.accountsType[0].id;
      }
    });
  },
  beforeDestroy() {
    this.$root.$off('on-finish', this.handleFinish);
  },
};
</script>

<style scoped>
.text-truncate {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.choose-accoount-option {
	margin: 0 !important;
	display: flex !important;
	padding: 32px !important;
	align-items: center;
	flex: 1 0 0;
	border-radius: 8px;
	border: 1px solid var(--Default-gray-300, #D1D5DB);
	background: var(--Default-white, #FFF);
	position: relative;
	cursor: pointer;
}

.choose-accoount-option.active {
	border: 1px solid var(--Default-green-600, #059669);
	background: var(--Default-green-50, #ECFDF5);
}

.choose-accoount-option.active .icon-tick path {
	fill: #059669;
}

.icon-tick {
	position: absolute;
	right: 16px;
	top: 16px;
}

/* Warning Note */
.note-warning {
	border-radius: 8px;
	background: #FFF3E6;
	display: flex;
	padding: 16px;
	align-items: center;
	gap: 16px;
	align-self: stretch;
}

.note-warning icon {
	width: 20px;
	height: 20px;
}

.note-warning p {
	color: var(--Default-yellow-700, #B45309) !important;
}
</style>
